import React, { FC, Fragment, useContext, useState } from 'react';
import { File as DenthubFile, FileSource } from 'models/file';
import LinearProgressBarWithLabel from 'components/shared/linear-progress-bar-with-label/linear-progress-bar-with-label.component';
import FileDropBoxArea from 'components/shared/file-drop-box-area/file-drop-box-area.component';
import Loader from 'components/shared/loader/loader.component';
import { Box } from '@mui/system';
import FileGrid from 'components/shared/file-upload-area/file-grid.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import axios, { AxiosProgressEvent, CancelTokenSource } from 'axios';
import { useUploadFailureHandler } from 'hooks/useUploadFailureHandler';
import { startUploadAttempt } from 'utils/upload-failure';
import { useMutation } from '@apollo/client';
import { deleteFileTemplate } from 'apollo/templates/files';
import { CONSTANTS } from 'config/constants';
import Cookies from 'js-cookie';
import { OrderInputType } from 'models/order-input/shared';
import { SupplierOrderContext } from 'pages/order-flow/supplier-order/supplier-order-context';

const OrderFiles: FC = () => {
    const { order, updateOrder } = useContext(SupplierOrderContext);
    const [files, setFiles] = useState<DenthubFile[]>(order.files || []);
    const [progress, setProgress] = useState<number>(0);
    const [uploading, setUploading] = useState(false);
    // Derived from the current order, deliberately not stored. This used to be state
    // initialised to the staging prefix and updated inside a useEffect with an empty
    // dependency array, so a component mounted while the order was still NEW kept
    // uploading to temp/uploads for the rest of its life -- including after the order
    // existed, where nothing repoints those File records (DD-3252 Phase 0.3).
    // Falls back to the staging prefix when the final path is not yet computable:
    // order creation promotes those objects, an undefined-laden key is unreachable.
    const canUseFinalDirectory = order.inputType !== OrderInputType.NEW && !!order.customer?._id && !!order._id;
    const uploadDirectory = canUseFinalDirectory
        ? `organizations/${order.customer?._id}/orders/${order._id}`
        : 'temp/uploads';
    const [_, setCancelToken] = useState<CancelTokenSource | null>(null);
    const { showBoundary } = useDenthubErrorBoundary();
    const handleUploadFailure = useUploadFailureHandler();

    const [deleteFile, { loading: deleteFileLoading }] = useMutation(deleteFileTemplate, {
        fetchPolicy: 'no-cache',
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ deleteFile }) => {
            const newFiles = files.filter((item) => item._id !== deleteFile);
            setFiles(newFiles);
            updateOrder({ ...order, files: newFiles });
        },
    });

    const handleChange = async (newFiles: File[]) => {
        const attempt = startUploadAttempt(newFiles ?? []);
        try {
            if (!newFiles || newFiles.length === 0) {
                return;
            }
            setUploading(true);
            const source = axios.CancelToken.source();
            setCancelToken(source);
            const formData = new FormData();
            for (const file of newFiles) {
                formData.append('files', file, file.name);
            }

            const url = `${CONSTANTS.DOC_API_URI}/upload/files?directory=${uploadDirectory}&source=${FileSource.ORDER_ATTACHMENTS}`;
            const response = await axios.post(url, formData, {
                headers: {
                    Authorization: Cookies.get(CONSTANTS.TOKEN_KEY),
                },
                onUploadProgress: (ev: AxiosProgressEvent) => {
                    attempt.sentBytes = ev.loaded;
                    setProgress((ev.loaded / (ev.total || 100)) * 100);
                },
                cancelToken: source.token,
            });
            if (response.status === 200 && response.data?.length) {
                setFiles([...files, ...response.data]);
                updateOrder({ ...order, files: [...files, ...response.data] });
            }
        } catch (error) {
            handleUploadFailure(error, {
                upload: 'supplier-delivery-files',
                attempt,
                retry: () => handleChange(newFiles),
            });
        } finally {
            setProgress(0);
            setUploading(false);
        }
    };

    const handleDeleteFile = (fileId: string) => {
        if (fileId) {
            deleteFile({ variables: { _id: fileId, orderId: order._id } });
        }
    };

    return (
        <Fragment>
            <Box mb={2}>
                <FileDropBoxArea handleChange={handleChange} />
            </Box>
            {uploading ? (
                <LinearProgressBarWithLabel progress={progress} />
            ) : deleteFileLoading ? (
                <Loader />
            ) : (
                <Box display="flex">
                    <FileGrid files={files} onDelete={handleDeleteFile} />
                </Box>
            )}
        </Fragment>
    );
};

export default OrderFiles;
